import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/components/ScrollReveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useFaqs } from "@/hooks/usePublicContent";
import { useLocale } from "@/i18n/LocaleProvider";

export interface FaqEntry {
  question: string;
  answer: string;
}

export const faqs: FaqEntry[] = [
  {
    question: "Why replace SaaS with custom software at all?",
    answer:
      "Standard SaaS was the right answer when software had to be generic. Today it mostly means you bend your process around someone else's template, pay per seat as you grow, and wait for features on a roadmap you do not control. A custom tool follows your process, contains only what your team actually uses, and belongs to you.",
  },
  {
    question: "Is custom software really cheaper than a SaaS subscription?",
    answer:
      "Over the life of a tool, usually yes. A subscription is a permanent cost that scales with every new user and every added module, while a bespoke build is a one-time project with predictable maintenance. Teams paying four or five figures a month across overlapping tools typically reach break-even within the first one to two years, and then own the asset.",
  },
  {
    question: "What does a custom SaaS or web app cost?",
    answer:
      "Every build is scoped individually, so there is no fixed price list. Most focused MVPs start in the low five figures, while larger multi-tenant platforms grow from there. After a short discovery call we give you a clear, fixed scope and a predictable roadmap, no vague hourly estimates that quietly balloon over months.",
  },
  {
    question: "Who owns the code and infrastructure?",
    answer:
      "You do, end to end. You own the entire codebase, the repositories and the infrastructure we set up for you. There is no vendor lock-in and no black box, you can take the project to any team at any time.",
  },
  {
    question: "How long does a project take?",
    answer:
      "A typical MVP ships in 6 to 12 weeks, depending on scope and integrations. We work in clear milestones with regular previews, so you always see progress and can steer the build as it takes shape.",
  },
  {
    question: "What kind of software do you build?",
    answer:
      "Bespoke, production-grade software: multi-tenant SaaS platforms, customer and client portals, web apps and dashboards, internal tools and automations, CRMs, and AI agents and copilots. If you can picture it, we can build it, we do not resell templates.",
  },
  {
    question: "Do you build AI features into products?",
    answer:
      "Yes. AI is native to how we build, from assistants and copilots to retrieval-augmented (RAG) search and workflow automation, wired directly into your product and data rather than bolted on as a gimmick.",
  },
  {
    question: "Do you work with clients outside Cyprus?",
    answer:
      "Absolutely. DevCore is a Cyprus-registered studio working with clients internationally and remotely. You get a direct line to the senior engineers actually building your product, wherever you are based.",
  },
  {
    question: "What happens after launch?",
    answer:
      "Launch is a milestone, not the end. We harden security (RLS, encryption, audit logging), set up automated tests and CI, and can continue with iteration, support and new features as your product grows.",
  },
];

export interface FAQSectionProps {
  /** Section id (anchor). Defaults to "faq". */
  id?: string;
  /** Mono kicker above the headline. Defaults to the homepage i18n label. */
  label?: string;
  /** First (solid) part of the headline. Defaults to the homepage i18n heading. */
  headingA?: string;
  /** Second (gradient) part of the headline. Omit for a single-color headline. */
  headingB?: string;
  /** Optional subtitle node. Pass `null` to hide it. */
  subtitle?: ReactNode | null;
  /** FAQ entries. When omitted, the homepage uses live DB data with a static fallback. */
  items?: FaqEntry[];
}

const FAQSection = ({
  id = "faq",
  label,
  headingA,
  headingB,
  subtitle,
  items: itemsProp,
}: FAQSectionProps = {}) => {
  const { t } = useTranslation();
  const { lang, localize } = useLocale();
  const { data } = useFaqs(lang);

  const isCustom = Boolean(itemsProp && itemsProp.length > 0);

  const items: FaqEntry[] = isCustom
    ? (itemsProp as FaqEntry[])
    : data && data.length > 0
      ? data.map((f) => ({ question: f.question, answer: f.answer }))
      : faqs;

  // Only lead-magnet/custom callers get a kicker; the homepage headline stands alone.
  const resolvedLabel = label;
  const resolvedHeadingA = headingA ?? t("faq.headingA");
  const resolvedHeadingB = headingB ?? (isCustom ? undefined : t("faq.headingB"));

  const defaultSubtitle = (
    <>
      {t("faq.subtitle")}{" "}
      <a
        href={localize("/contact")}
        className="text-accent underline-offset-4 hover:underline transition-colors"
      >
        {t("faq.getInTouch")}
      </a>
      .
    </>
  );
  const resolvedSubtitle =
    subtitle !== undefined ? subtitle : isCustom ? null : defaultSubtitle;

  // True only inside the build-time prerender browser (set via addInitScript).
  const isPrerender =
    typeof window !== "undefined" &&
    (window as unknown as { __PRERENDER__?: boolean }).__PRERENDER__ === true;

  const accordionItems = items.map((faq, i) => (
    <AccordionItem
      key={faq.question}
      value={`item-${i}`}
      className="border-0 px-6 md:px-8"
    >
      <AccordionTrigger className="py-6 text-left text-base md:text-lg font-medium text-foreground hover:no-underline hover:text-accent transition-colors duration-200">
        {faq.question}
      </AccordionTrigger>
      <AccordionContent className="pb-6 text-muted-foreground text-base leading-relaxed">
        {faq.answer}
      </AccordionContent>
    </AccordionItem>
  ));
  return (
    <section
      id={id}
      className="relative section-y bg-background overflow-clip"
    >
      <div className="absolute inset-0 tech-grid opacity-[0.1] pointer-events-none" />
      <div className="absolute -left-20 top-1/4 w-96 h-96 bg-primary/[0.07] blur-[120px] rounded-full pointer-events-none" />

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-3 gap-10 lg:gap-16">
          {/* heading */}
          <div className="lg:col-span-1 lg:sticky lg:top-28 lg:self-start">
            <ScrollReveal>
              {resolvedLabel ? <p className="mono-label mb-6">{resolvedLabel}</p> : null}
              <h2 className="text-[1.55rem] sm:text-3xl md:text-5xl font-bold text-foreground leading-[1.1] tracking-tight">
                {resolvedHeadingA}
                {resolvedHeadingB ? (
                  <>
                    {" "}
                    <span className="gradient-text">{resolvedHeadingB}</span>
                  </>
                ) : null}
              </h2>
              {resolvedSubtitle ? (
                <p className="mt-6 text-muted-foreground text-[15px] sm:text-lg leading-relaxed">
                  {resolvedSubtitle}
                </p>
              ) : null}
            </ScrollReveal>
          </div>

          {/* accordion */}
          <div className="lg:col-span-2">
            <ScrollReveal delay={120}>
              {/* During the build-time prerender snapshot every answer is
                  expanded so crawlers / JS-less AI bots read the full content.
                  At real runtime (__PRERENDER__ undefined) the accordion is a
                  normal single-collapsible — all answers start collapsed. */}
              {isPrerender ? (
                <Accordion
                  type="multiple"
                  defaultValue={items.map((_, i) => `item-${i}`)}
                  className="rounded-3xl border border-border bg-card/50 backdrop-blur-xl divide-y divide-border/60 overflow-hidden shadow-2xl"
                >
                  {accordionItems}
                </Accordion>
              ) : (
                <Accordion
                  type="single"
                  collapsible
                  className="rounded-3xl border border-border bg-card/50 backdrop-blur-xl divide-y divide-border/60 overflow-hidden shadow-2xl"
                >
                  {accordionItems}
                </Accordion>
              )}
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
